import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";
import { Backdrop } from "@material-ui/core";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 4,
  },
  colors: {
    primary: {
      main: "#35555C",
      dark: "#465A59",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#4D4D4D",
      dark: "#875735",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#C82E2D",
    },
    info: {
      main: "#00AAE6",
      light: "#D6F1FB",
    },
    text: {
      primary: "#331E11",
      secondary: "#333333",
      hint: "#757575",
    },
    background: "#FFFFFF",
    border: "#949494",
    hover: "#FAFAFA",
    placeholder: "#331E11",
    transparent: "#00000000",
    divider: "#DDE4E6",
  },
};

const defaultPathParams: React.SVGProps<SVGPathElement> = {
  stroke: "currentColor",
  strokeWidth: "1",
  fill: "none",
};
function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const volviano = {
  themeName: "volviano",
  brand: BrandsEnum.VolviaNo,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.background,
      paper: variables.colors.divider,
    },
    background: {
      default: variables.colors.background,
      paper: variables.colors.divider,
    },
    divider: variables.colors.border,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily("Volvo Novum-Regular"),
    color: variables.colors.secondary.main,
    h1: {
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.secondary,
      fontWeight: 400,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
    },
    h2: {
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.secondary,
      fontWeight: 400,
      textAlign: "center" as "center",
      paddingBottom: "2rem",
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
    },
    h3: {
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
      color: variables.colors.secondary.main,
      fontWeight: 400,
      textDecoration: "underline",
    },
    h4: {
      color: variables.colors.secondary.main,
      fontWeight: 400,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      fontFamily: getFontFamily("Volvo Novum-Medium"),
      fontSize: "1.25rem",
      color: variables.colors.secondary.main,
      fontWeight: 900,
    },
    h6: {
      color: variables.colors.secondary.main,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    subtitle2: {
      fontFamily: getFontFamily("Volvo Novum-Medium"),
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.secondary.main,
      fontWeight: 700,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.secondary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.secondary.main,
    },
  },
  addOnCard: {
    header: {
      textColor: variables.colors.text.secondary,
    },
    addText: {
      color: variables.colors.primary.main,
      fontWeight: 500,
    },
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
      descriptionText: {
        colorCard: variables.colors.secondary.main,
      },
    },
    cardSelected: {
      backgroundColor: "#F6F8F9",
      border: `2px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      addText: {
        color: variables.colors.primary.main,
        fontWeight: 500,
      },
    },
  },
  coverageSelect: {
    root: {
      borderColor: "#DDE4E6",
      CoverageHeaderName: {
        color: variables.colors.text.secondary,
      },
      CoverageHeaderPrice: {
        color: variables.colors.text.secondary,
      },
      CoverageItemText: {
        color: variables.colors.text.secondary,
      },
      CoverageWrapper: {
        width: "18%",
        [theme.breakpoints.up("md")]: {
          width: "22%",
        },
      },
    },
    recommended: {
      backgroundColor: "#35555C",
      corderColor: "#35555C",
    },
    selected: {
      mobileCoverageSelectedWith: "46%",
      backgroundColor: "#FFFFFF",
      border: "2px solid #35555C",
      CoverageHeaderName: {
        color: variables.colors.text.secondary,
      },
      CoverageHeaderPrice: {
        color: variables.colors.text.secondary,
      },
      CoverageItem: {
        [theme.breakpoints.up("md")]: {
          paddingLeft: 0,
        },
      },
    },
    descriptionCard: {
      backgroundColor: "#F6F8F9",
      borderColor: "#F6F8F9",
      textColor: variables.colors.secondary.main,
    },
  },

  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    header: {
      backgroundColor: "#550077",
      textAlign: theme.typography.h1.textAlign,
      padding: `${theme.spacing(3)}px 0`,
    },
    MuiTypography: {
      colorSecondary: {
        color: variables.colors.secondary.main,
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.primary.main,
        fontWeight: 400,
      },
    },
    MuiDivider: {
      root: {
        backgroundColor: variables.colors.divider,
      },
    },
    MuiButton: {
      root: {
        textTransform: "none" as TextTransformProperty,
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
        fontWeight: 700,
        "a&": {
          "&:hover": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
          },
          "&:active": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
          },
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.main,
          textDecoration: "none",
          fontWeight: 700,
        },
      },
      containedPrimary: {
        fontWeight: 700,
      },
      outlinedSecondary: {
        "&:hover": {
          border: "2px solid " + variables.colors.primary.dark,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.dark,
        },
        "&:active": {
          border: "2px solid " + variables.colors.primary.dark,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.dark,
        },
        color: variables.colors.primary.main,
        border: "2px solid " + variables.colors.primary.main,
        fontWeight: 700,
        // this is just to make link styling same as button if link has button classes
        // needed to override link styling by external CSS by making selector more specific
        "a&": {
          "&:hover": {
            border: "2px solid " + variables.colors.primary.dark,
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
          },
          "&:active": {
            border: "2px solid " + variables.colors.primary.dark,
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
          },
          color: variables.colors.primary.main,
          border: "2px solid " + variables.colors.primary.main,
          textDecoration: "none",
          fontWeight: 700,
        },
      },
      text: {
        "&:hover": {
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
        },
        color: variables.colors.primary.main,
      },
      textSecondary: {
        color: variables.colors.primary.main,
        "&:hover": {
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          BackgroundColor: variables.colors.transparent,
        },
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
    },
    MuiFormLabel: {
      root: {
        fontWeight: 400,
        lineHeight: "1.5rem",
        fontFamily: getFontFamily("Volvo Novum-Medium"),
        color: variables.colors.text.secondary,
        "&$focused": {
          color: variables.colors.text.secondary,
        },
      },
    },
    MuiInputLabel: {
      formControl: {
        fontFamily: getFontFamily("Volvo Novum-Medium"),
        position: "relative" as PositionProperty,
        transform: "none",
        color: variables.colors.text.primary,
        lineHeight: "1.5rem", // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: variables.common.borderRadius,
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 1,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiInputBase: {
      root: {
        color: variables.colors.text.primary,
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
      },
      icon: {
        color: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          // to increase specificy
          color: "#757575",
          textDecoration: "none",
          "&:hover": {
            color: "#757575",
          },
          "&:active": {
            color: "#757575",
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.text.secondary,
        border: "1px solid " + variables.colors.border,
        "&$selected": {
          color: variables.colors.placeholder,
          backgroundColor: variables.colors.transparent,
          fontWeight: 700,
          border: "2px solid " + variables.colors.primary.main,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiPickersDay: {
      daySelected: {
        "& :hover": {
          color: variables.colors.primary.contrastText,
        },
      },
    },
    MuiCheckbox: {
      colorSecondary: {
        "&.Mui-checked": {
          color: "#35555C",
        },
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
    MuiPaper: {
      root: {
        //backgroundColor: variables.colors.background,
      },
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    hoverBackgroundColor: variables.colors.hover,
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#F6F8F9",
    activeFontWeight: 500,
  },
  footerBar: {
    backgroundColor: "#1D1F20",
    textColor: variables.colors.secondary.contrastText,
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      color: variables.colors.primary.main,
    },
  },
  icons: {
    infoIcon: (
      <path
        {...defaultPathParams}
        paintOrder='normal'
        strokeLinecap='butt'
        strokeMiterlimit='10'
        d='M 10.75,8.1503401 V 16.34966 m 10,-5.59966 c 0,5.522848 -4.477152,10 -10,10 -5.5228475,0 -10,-4.477152 -10,-10 0,-5.5228475 4.4771525,-10 10,-10 5.522848,0 10,4.4771525 10,10 M 11.402081,5.164 c 0,0.4120491 -0.334032,0.7460809 -0.746081,0.7460809 -0.412049,0 -0.7460809,-0.3340318 -0.7460809,-0.7460809 0,-0.4120491 0.3340319,-0.7460809 0.7460809,-0.7460809 0.412049,0 0.746081,0.3340318 0.746081,0.7460809'
      />
    ),
    helpIcon: (
      <path
        {...defaultPathParams}
        strokeDasharray='none'
        paintOrder='normal'
        strokeLinecap='butt'
        strokeMiterlimit='4'
        strokeOpacity='1'
        fillOpacity='1'
        d='m 9.938,6.526 c 1.861,-0.823 4.275,-0.727 5.1,0.607 0.825,1.334 0.255,2.883 -1.157,4.079 -1.412,1.196 -1.88,1.847 -1.88,2.788 m 9.999019,-2 c 0,5.522847 -4.477153,10 -10,10 -5.5228475,0 -10,-4.477153 -10,-10 0,-5.5228475 4.4771525,-10 10,-10 5.522847,0 10,4.4771525 10,10 m -10.00002,4.914346 c -0.410685,0 -0.75167,0.340985 -0.75167,0.75167 0,0.03362 0.0017,0.06624 0.0059,0.09836 v 0.0015 c 0.04876,0.367965 0.368545,0.651804 0.7458,0.651804 0.320751,0 0.600313,-0.204508 0.706158,-0.493283 0.02965,-0.08092 0.04551,-0.168432 0.04551,-0.258387 0,-0.05665 -0.0071,-0.112575 -0.01909,-0.165897 -0.07589,-0.336227 -0.378404,-0.585773 -0.73258,-0.585773 z'
      />
    ),
    selectIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 18,9 12,15 6,9' />,
    arrowUpIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 18,15 12,9 6,15' />,
    dateTimeIcon: (
      <path
        {...defaultPathParams}
        strokeLinecap='square'
        strokeMiterlimit='10'
        d='m 2.4615,5.7769 h 20.577 V 21.5526 H 2.4615 Z m 20.577,3.4295 H 2.4615 M 17.5513,2.3474 V 5.7769 M 7.9487,2.3474 v 3.4295'
      />
    ),

    addIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 20,12 H 4 m 8,-8 v 16' />,
    removeIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 18,18 6,6 M 18,6 6,18' />,

    //crossIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 18,18 6,6 M 18,6 6,18' />,
    crossIcon: (
      <svg width='24' height='25' viewBox='0 0 24 25' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g opacity='0.4'>
          <path
            d='M12 12.7462L17.7045 18.4507L18 18.1552L12.2955 12.4507L18 6.74618L17.7045 6.45068L12 12.1552L6.2955 6.45068L6 6.74618L11.7045 12.4507L6 18.1552L6.2955 18.4507L12 12.7462Z'
            fill='#35555C'
            stroke='#35555C'
            stroke-width='0.5'
          />
        </g>
      </svg>
    ),
    //checkmarkIcon: <path {...defaultPathParams} d='M 4,12.714 9.143,17.857 20,7' />,
    checkmarkIcon: (
      <svg width='24' height='25' viewBox='0 0 24 25' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M2 12.4507C2 14.4285 2.58649 16.3619 3.6853 18.0064C4.78411 19.6509 6.3459 20.9326 8.17316 21.6895C10.0004 22.4464 12.0111 22.6444 13.9509 22.2585C15.8907 21.8727 17.6725 20.9203 19.0711 19.5218C20.4696 18.1232 21.422 16.3414 21.8078 14.4016C22.1937 12.4618 21.9957 10.4511 21.2388 8.62385C20.4819 6.79659 19.2002 5.2348 17.5557 4.13599C15.9112 3.03717 13.9778 2.45068 12 2.45068C9.34783 2.45068 6.80429 3.50425 4.92893 5.37962C3.05356 7.25498 2 9.79852 2 12.4507Z'
          fill='#DDE4E6'
          stroke='#DDE4E6'
          stroke-width='0.5'
        />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M11.038 15.9176L18.381 8.51758L18.613 8.74758L11.038 16.3836L6.61304 11.9176L6.84504 11.6876L11.038 15.9176Z'
          fill='#333333'
          stroke='#35555C'
          stroke-width='0.75'
        />
      </svg>
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <g id="Group_12436" data-name="Group 12436" transform="translate(-1060 -323.445)">
        <path id="Path_4742" data-name="Path 4742" d="M12,24A12,12,0,1,0,0,12,12,12,0,0,0,12,24Z" transform="translate(1084 323.445) rotate(90)" fill="#dde4e6" fill-rule="evenodd"/>
        <path id="Checkmark" d="M6.425,18.4,13.768,11l.232.23L6.425,18.866,2,14.4l.232-.23Z" transform="translate(1064 320.513)" fill="#333" stroke="#070708" stroke-width="0.5" fill-rule="evenodd"/>
      </g>
    </svg>`),
    checkmarkSelectedOptionIcon: (
      <>
        <path fill='#35555C' stroke='#35555C' d='M-1.2071-.5H48.5v49.7071z' transform='matrix(1.24418 0 0 1.2493 3.00373 1.01704)' />
        <polygon id='Path' points='6 11.17 1.83 7 0.41 8.41 6 14 18 2 16.59 0.59' transform='translate(12 3) scale(0.5)' fill='#FFFFFF' />
      </>
    ),
    backIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='m 15,6 -6,6 6,6' />,
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: "#E3E9F5",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  divider: {
    backgroundColor: "#DDE4E6",
  },
  statusIndicator: {
    borderRadius: 4,
  },
};

export default createTheme(volviano);
